import { useEffect, useState } from "react";
import { usePageTitle } from "@/hooks/usePageTitle";
import { Link } from "react-router-dom";
import {
  Bell, Eye, ShieldCheck, History, Trash2, Phone, Lock, User,
} from "lucide-react";
import { TeacherSidebar } from "@/components/teacher/dashboard/TeacherSidebar";
import { StudentSidebar } from "@/components/student/dashboard/StudentSidebar";
import { SidebarProvider } from "@/components/ui/sidebar";
import SearchResultsPage from "@/pages/public/SearchResultsPage";
import { DashboardTopNav } from "@/components/layout/DashboardTopNav";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useCurrentUser } from "@/hooks/use-current-user";
import { useAuth } from "@/hooks/use-auth-compat";
import { useRole } from "@/contexts/RoleContext";
import { useTeacherProfile } from "@/contexts/TeacherProfileContext";
import { updateUserProfile } from "@/services/UserManagmentServices";
import { CountryCodeSelect, parseStoredPhone, type Country } from "@/components/CountryCodeSelect";
import { useEmailPreferences, useUpdateEmailPreferences } from "@/hooks/use-email-preferences";
import { toast } from "@/hooks/use-toast";
import { useGoalProfile, useGoalHistory } from "@/hooks/useGoalProfile";
import { useExamTargets } from "@/hooks/useExamTargets";
import { useEnrichment } from "@/hooks/useEnrichment";
import { useStudentSettings, useUpdateStudentSettings } from "@/hooks/use-student-settings";
import { useTeacherSettings, useUpdateTeacherSettings } from "@/hooks/use-teacher-settings";
import {
  useInitiateStudentDeletion,
  useInitiateTeacherDeletion,
  useInitiateFullDeletion,
} from "@/hooks/use-account-deletion";
import { ChangePasswordDialog } from "@/components/settings/ChangePasswordDialog";
import {
  gradeLabel, modeLabel, timelineLabel, learningStyleLabel, DAYS, TIMES,
} from "@/lib/goalOptions";
import { currencySymbol } from "@/lib/currency";

// ────────────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────────────

/** "First L." — shown to teachers in inquiries */
function shortName(name?: string | null): string {
  if (!name) return "Student";
  const base = name.includes("@") ? name.split("@")[0].replace(/[._]/g, " ") : name;
  const parts = base.trim().split(/\s+/);
  const first = parts[0] ? parts[0][0].toUpperCase() + parts[0].slice(1) : "Student";
  const lastInitial = parts.length > 1 ? ` ${parts[parts.length - 1][0].toUpperCase()}.` : "";
  return first + lastInitial;
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between px-4 py-2.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-sm text-foreground text-right">{value}</span>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Shared sections
// ────────────────────────────────────────────────────────────────────────────


// ────────────────────────────────────────────────────────────────────────────
// General tab
// ────────────────────────────────────────────────────────────────────────────

function GeneralTab({
  name,
  phoneNumber,
  email,
  onSaved,
}: {
  name: string;
  phoneNumber?: string | null;
  email?: string | null;
  onSaved: () => void;
}) {
  const auth = useAuth();
  const initiateFullDeletion = useInitiateFullDeletion();

  const [editedName, setEditedName] = useState(name);
  const [country, setCountry] = useState<Country>(() => parseStoredPhone(phoneNumber || "").country);
  const [mobileNumber, setMobileNumber] = useState(() => parseStoredPhone(phoneNumber || "").local);
  const [saving, setSaving] = useState(false);
  // SCRUM-365: residence country is neither captured nor displayed.

  useEffect(() => {
    setEditedName(name);
    const parsed = parseStoredPhone(phoneNumber || "");
    setCountry(parsed.country);
    setMobileNumber(parsed.local);
  }, [name, phoneNumber]);

  const dirty = editedName.trim() !== (name || "").trim()
    || `${country.dial}${mobileNumber.trim()}` !== (phoneNumber || "");

  const handleSave = async () => {
    const token = await auth.getToken();
    if (!token) {
      toast({ title: "Session expired. Please log in again.", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      await updateUserProfile(token, {
        name: editedName.trim(),
        phone: mobileNumber.trim() ? `${country.dial}${mobileNumber.trim()}` : "",
      });
      toast({ title: "Profile updated" });
      onSaved();
    } catch (err) {
      const message = err instanceof Error ? err.message : "";
      toast({
        title: "Failed to save profile",
        // Server message, e.g. a 400 for an unknown/inactive country code.
        ...(message && message !== "Failed to update profile" ? { description: message } : {}),
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleFullDelete = () => {
    initiateFullDeletion.mutate(undefined, {
      onSuccess: () =>
        toast({
          title: "Check your email",
          description: "We've sent a confirmation link to delete your entire account.",
        }),
      onError: () =>
        toast({ title: "Request failed", description: "Please try again.", variant: "destructive" }),
    });
  };

  return (
    <div className="space-y-8">
      {/* Identity */}
      <section>
        <div className="flex items-center gap-2 mb-1">
          <User className="w-4 h-4 text-muted-foreground" />
          <h2 className="text-base font-semibold text-foreground">Identity</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-4">Your account details.</p>
        <div className="rounded-xl border border-border/60 bg-card p-4 space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Full Name</Label>
            <Input
              value={editedName}
              onChange={(e) => setEditedName(e.target.value)}
              placeholder="Your full name"
              className="h-10"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground flex items-center gap-1.5">
              <Phone className="w-3.5 h-3.5" /> Phone Number
            </Label>
            <div className="flex gap-2">
              <CountryCodeSelect value={country} onChange={setCountry} />
              <Input
                type="tel"
                inputMode="numeric"
                value={mobileNumber}
                onChange={(e) => setMobileNumber(e.target.value.replace(/\D/g, "").slice(0, 12))}
                placeholder="9876543210"
                maxLength={12}
                className="h-10 flex-1"
              />
            </div>
          </div>
          <div className="flex items-center justify-between px-0 py-1">
            <span className="text-xs text-muted-foreground">Email</span>
            <span className="text-sm font-medium text-foreground">{email ?? "—"}</span>
          </div>
          <div className="flex justify-end">
            <Button size="sm" onClick={handleSave} disabled={!dirty || saving} className="cursor-pointer">
              {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      </section>

      {/* Auth provider */}
      <section>
        <div className="flex items-center gap-2 mb-1">
          <Lock className="w-4 h-4 text-muted-foreground" />
          <h2 className="text-base font-semibold text-foreground">Password</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Manage your sign-in credentials.
        </p>
        <div className="rounded-xl border border-border/60 bg-card divide-y divide-border/40">
          <div className="flex items-center justify-between px-4 py-3">
            <div>
              <span className="text-sm font-medium">Email / Password</span>
              <p className="text-sm text-muted-foreground">Change your account password</p>
            </div>
            <ChangePasswordDialog />
          </div>
        </div>
      </section>

      {/* Danger zone — full account */}
      <section>
        <div className="flex items-center gap-2 mb-1">
          <Trash2 className="w-4 h-4 text-destructive" />
          <h2 className="text-base font-semibold text-foreground">Delete everything</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Permanently remove your account and all your data across all roles.
        </p>
        <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-4">
          <p className="text-sm text-foreground mb-3">
            This deletes all your data — goals, inquiries, teacher profile, and offerings. A
            confirmation email will be sent first. This action cannot be undone.
          </p>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="destructive"
                size="sm"
                disabled={initiateFullDeletion.isPending}
              >
                Delete everything
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete your entire account?</AlertDialogTitle>
                <AlertDialogDescription>
                  This removes all your data across all roles. We'll email you a confirmation
                  link — clicking it executes the deletion permanently. This cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Keep my account</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  onClick={handleFullDelete}
                >
                  Send confirmation email
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </section>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Student tab
// ────────────────────────────────────────────────────────────────────────────

function StudentTab({ displayName }: { displayName: string }) {
  const { data: prefs } = useEmailPreferences();
  const updateEmailPrefs = useUpdateEmailPreferences();
  const { data: settings, isLoading: settingsLoading } = useStudentSettings();
  const updateSettings = useUpdateStudentSettings();
  const initiateStudentDeletion = useInitiateStudentDeletion();

  const { data: goal } = useGoalProfile();
  const { targets: exams } = useExamTargets();
  const { availability, prefs: learningPrefs, secondary } = useEnrichment();
  const { data: history = [] } = useGoalHistory();

  if (settingsLoading) return null;

  const budget =
    goal?.budgetMin != null && goal?.budgetMax != null
      ? `${currencySymbol(goal.currency)}${goal.budgetMin}–${goal.budgetMax} / session`
      : "—";
  const avail =
    availability.length === 0
      ? "Not set"
      : availability
          .map(
            (s) =>
              `${DAYS.find((d) => d.value === s.day)?.label} ${TIMES.find((t) => t.value === s.time)?.label}`,
          )
          .join(", ");

  const handleToggle = (field: "inquiryEmails" | "reminderEmails", value: boolean) => {
    if (!prefs) return;
    updateEmailPrefs.mutate(
      { ...prefs, [field]: value },
      {
        onSuccess: () => toast({ title: "Preferences updated" }),
        onError: () => toast({ title: "Failed", variant: "destructive" }),
      },
    );
  };

  const handleSettingsToggle = (
    field: "waitlistAlerts" | "weeklyDigest" | "smsOptIn" | "allowSearchHistory",
    value: boolean,
  ) => {
    updateSettings.mutate(
      { [field]: value },
      {
        onSuccess: () => toast({ title: "Preferences updated" }),
        onError: () => toast({ title: "Failed", variant: "destructive" }),
      },
    );
  };

  const handleStudentDelete = () => {
    initiateStudentDeletion.mutate(undefined, {
      onSuccess: () =>
        toast({
          title: "Check your email",
          description: "A confirmation link to delete your student data has been sent.",
        }),
      onError: () =>
        toast({ title: "Request failed", description: "Please try again.", variant: "destructive" }),
    });
  };

  return (
    <div className="space-y-8">
      {/* What teachers see */}
      <section>
        <div className="flex items-center gap-2 mb-1">
          <Eye className="w-4 h-4 text-muted-foreground" />
          <h2 className="text-base font-semibold text-foreground">What teachers see</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          When you message a teacher, they see this profile — never your email, phone, or full
          name.
        </p>
        <div className="rounded-xl border border-border/60 bg-card divide-y divide-border/40">
          <Field label="Shown as" value={shortName(displayName)} />
          <Field label="Subject" value={goal?.subject ?? "—"} />
          <Field label="Grade" value={gradeLabel(goal?.gradeBand) || "—"} />
          <Field label="Curriculum" value={goal?.curriculum ?? "—"} />
          <Field label="Goal" value={goal?.goalType ?? "—"} />
          <Field label="Mode" value={modeLabel(goal?.modePreference) || "—"} />
          <Field label="Budget" value={budget} />
          <Field label="Start" value={timelineLabel(goal?.startTimeline) || "—"} />
          {exams.length > 0 && (
            <Field
              label="Exam targets"
              value={exams.map((e) => `${e.examName} (${e.examDate})`).join(", ")}
            />
          )}
          {learningPrefs.learningStyle && (
            <Field label="Learning style" value={learningStyleLabel(learningPrefs.learningStyle)} />
          )}
          {secondary.length > 0 && (
            <Field label="Also learning" value={secondary.map((s) => s.subjectName).join(", ")} />
          )}
          <Field label="Availability" value={avail} />
        </div>
      </section>

      {/* Email notifications */}
      {prefs && (
        <section>
          <div className="flex items-center gap-2 mb-1">
            <Bell className="w-4 h-4 text-muted-foreground" />
            <h2 className="text-base font-semibold text-foreground">Email Notifications</h2>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            Control which emails you receive.
          </p>
          <div className="rounded-xl border border-border/60 bg-card divide-y divide-border/40">
            <div className="flex items-center justify-between px-4 py-3">
              <div>
                <Label className="text-sm font-medium">Inquiry notifications</Label>
                <p className="text-sm text-muted-foreground">
                  Get notified when you receive new messages
                </p>
              </div>
              <Switch
                checked={prefs.inquiryEmails}
                onCheckedChange={(v) => handleToggle("inquiryEmails", v)}
                className="cursor-pointer"
              />
            </div>
            <div className="flex items-center justify-between px-4 py-3">
              <div>
                <Label className="text-sm font-medium">Unread reminders</Label>
                <p className="text-sm text-muted-foreground">
                  Periodic reminders for unread messages
                </p>
              </div>
              <Switch
                checked={prefs.reminderEmails}
                onCheckedChange={(v) => handleToggle("reminderEmails", v)}
                className="cursor-pointer"
              />
            </div>
          </div>
        </section>
      )}

      {/* More notifications (real API) */}
      <section>
        <div className="flex items-center gap-2 mb-1">
          <Bell className="w-4 h-4 text-muted-foreground" />
          <h2 className="text-base font-semibold text-foreground">More notifications</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-4">Stay on top of seats and new matches.</p>
        <div className="rounded-xl border border-border/60 bg-card divide-y divide-border/40">
          <div className="flex items-center justify-between px-4 py-3">
            <div>
              <Label className="text-sm font-medium">Waitlist seat alerts</Label>
              <p className="text-sm text-muted-foreground">
                Email me when a seat opens in a class I'm waitlisted for
              </p>
            </div>
            <Switch
              checked={settings?.waitlistAlerts ?? true}
              onCheckedChange={(v) => handleSettingsToggle("waitlistAlerts", v)}
              className="cursor-pointer"
            />
          </div>
          <div className="flex items-center justify-between px-4 py-3">
            <div>
              <Label className="text-sm font-medium">Weekly recommendations</Label>
              <p className="text-sm text-muted-foreground">
                A weekly digest of new teachers matching your goal
              </p>
            </div>
            <Switch
              checked={settings?.weeklyDigest ?? false}
              onCheckedChange={(v) => handleSettingsToggle("weeklyDigest", v)}
              className="cursor-pointer"
            />
          </div>
          <div className="flex items-center justify-between px-4 py-3">
            <div>
              <Label className="text-sm font-medium">SMS / WhatsApp</Label>
              <p className="text-sm text-muted-foreground">
                Get the most important alerts by text
              </p>
            </div>
            <Switch
              checked={settings?.smsOptIn ?? false}
              onCheckedChange={(v) => handleSettingsToggle("smsOptIn", v)}
              className="cursor-pointer"
            />
          </div>
        </div>
      </section>

      {/* Privacy (real API) */}
      <section>
        <div className="flex items-center gap-2 mb-1">
          <ShieldCheck className="w-4 h-4 text-muted-foreground" />
          <h2 className="text-base font-semibold text-foreground">Privacy</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Control how your information is used.
        </p>
        <div className="rounded-xl border border-border/60 bg-card divide-y divide-border/40">
          <div className="flex items-center justify-between px-4 py-3">
            <div>
              <Label className="text-sm font-medium">Share my profile with teachers I message</Label>
              <p className="text-sm text-muted-foreground">
                Required — teachers need your goal to respond. Always on.
              </p>
            </div>
            <Switch checked disabled />
          </div>
          <div className="flex items-center justify-between px-4 py-3">
            <div>
              <Label className="text-sm font-medium">
                Use my activity to improve recommendations
              </Label>
              <p className="text-sm text-muted-foreground">
                Let BMC use your searches and saves to sharpen matches
              </p>
            </div>
            <Switch
              checked={settings?.allowSearchHistory ?? true}
              onCheckedChange={(v) => handleSettingsToggle("allowSearchHistory", v)}
              className="cursor-pointer"
            />
          </div>
        </div>
      </section>

      {/* Goal history */}
      <section>
        <div className="flex items-center gap-2 mb-1">
          <History className="w-4 h-4 text-muted-foreground" />
          <h2 className="text-base font-semibold text-foreground">Goal history</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          A record of your previous goals. Snapshots are never deleted.
        </p>
        {history.length === 0 ? (
          <div className="rounded-xl border border-border/60 bg-card p-4">
            <p className="text-sm text-muted-foreground">
              No archived goals yet.{" "}
              <Link to="/my-goal" className="text-primary font-medium cursor-pointer">
                View my goal
              </Link>
            </p>
          </div>
        ) : (
          <div className="rounded-xl border border-border/60 bg-card divide-y divide-border/40">
            {history.map((h) => {
              const snap = h.snapshot as Record<string, unknown>;
              const subject = (snap.subject as string) ?? "—";
              const grade = gradeLabel(snap.gradeBand as string);
              return (
                <div key={h.id} className="px-4 py-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-foreground">
                      {subject}
                      {grade ? ` · ${grade}` : ""}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {new Date(h.archivedAt).toLocaleDateString()}
                    </span>
                  </div>
                  {h.changeReason && (
                    <p className="text-xs text-muted-foreground mt-0.5">{h.changeReason}</p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Delete student data */}
      <section>
        <div className="flex items-center gap-2 mb-1">
          <Trash2 className="w-4 h-4 text-destructive" />
          <h2 className="text-base font-semibold text-foreground">Delete student data</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Remove your student profile, goals, saved teachers, and inquiry history.
          Your teacher account (if you have one) remains intact.
        </p>
        <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-4">
          <p className="text-sm text-foreground mb-3">
            A confirmation email will be sent first. Clicking the link permanently removes your
            student data. This cannot be undone.
          </p>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="destructive"
                size="sm"
                disabled={initiateStudentDeletion.isPending}
                className="cursor-pointer"
              >
                Delete student data
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete your student data?</AlertDialogTitle>
                <AlertDialogDescription>
                  This removes your goals, saved teachers, and inquiry history. A confirmation
                  email will be sent — clicking it executes the deletion. Your teacher account
                  (if any) is unaffected. This cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Keep my data</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  onClick={handleStudentDelete}
                >
                  Send confirmation email
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </section>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Teacher tab
// ────────────────────────────────────────────────────────────────────────────

function TeacherTab() {
  const { data: teacherPrefs, isLoading } = useTeacherSettings();
  const updateTeacherPrefs = useUpdateTeacherSettings();
  const initiateTeacherDeletion = useInitiateTeacherDeletion();

  if (isLoading) return null;

  const handleToggle = (
    field: "inquiryReceived" | "threadReply" | "profileReviewResult" | "weeklyInquiryDigest" | "unreadReminder",
    value: boolean,
  ) => {
    updateTeacherPrefs.mutate(
      { [field]: value },
      {
        onSuccess: () => toast({ title: "Preferences updated" }),
        onError: () => toast({ title: "Failed", variant: "destructive" }),
      },
    );
  };

  const handleTeacherDelete = () => {
    initiateTeacherDeletion.mutate(undefined, {
      onSuccess: () =>
        toast({
          title: "Check your email",
          description: "A confirmation link to delete your teacher data has been sent.",
        }),
      onError: () =>
        toast({ title: "Request failed", description: "Please try again.", variant: "destructive" }),
    });
  };

  return (
    <div className="space-y-8">
      {/* Teacher notification preferences */}
      <section>
        <div className="flex items-center gap-2 mb-1">
          <Bell className="w-4 h-4 text-muted-foreground" />
          <h2 className="text-base font-semibold text-foreground">Notification preferences</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Control which teacher-related emails you receive.
        </p>
        <div className="rounded-xl border border-border/60 bg-card divide-y divide-border/40">
          <div className="flex items-center justify-between px-4 py-3">
            <div>
              <Label className="text-sm font-medium">New inquiry received</Label>
              <p className="text-sm text-muted-foreground">
                Email me when a student sends an inquiry
              </p>
            </div>
            <Switch
              checked={teacherPrefs?.inquiryReceived ?? true}
              onCheckedChange={(v) => handleToggle("inquiryReceived", v)}
              className="cursor-pointer"
            />
          </div>
          <div className="flex items-center justify-between px-4 py-3">
            <div>
              <Label className="text-sm font-medium">Thread replies</Label>
              <p className="text-sm text-muted-foreground">
                Email me when a student replies in an active thread
              </p>
            </div>
            <Switch
              checked={teacherPrefs?.threadReply ?? true}
              onCheckedChange={(v) => handleToggle("threadReply", v)}
              className="cursor-pointer"
            />
          </div>
          <div className="flex items-center justify-between px-4 py-3">
            <div>
              <Label className="text-sm font-medium">Unread message reminders</Label>
              <p className="text-sm text-muted-foreground">
                Email me if I haven't read a message after a few days
              </p>
            </div>
            <Switch
              checked={teacherPrefs?.unreadReminder ?? true}
              onCheckedChange={(v) => handleToggle("unreadReminder", v)}
              className="cursor-pointer"
            />
          </div>
          <div className="flex items-center justify-between px-4 py-3">
            <div>
              <Label className="text-sm font-medium">Profile review result</Label>
              <p className="text-sm text-muted-foreground">
                Email me when an admin approves or rejects my profile
              </p>
            </div>
            <Switch
              checked={teacherPrefs?.profileReviewResult ?? true}
              onCheckedChange={(v) => handleToggle("profileReviewResult", v)}
              className="cursor-pointer"
            />
          </div>
          <div className="flex items-center justify-between px-4 py-3">
            <div>
              <Label className="text-sm font-medium">Weekly inquiry digest</Label>
              <p className="text-sm text-muted-foreground">
                A weekly summary of inquiry activity
              </p>
            </div>
            <Switch
              checked={teacherPrefs?.weeklyInquiryDigest ?? false}
              onCheckedChange={(v) => handleToggle("weeklyInquiryDigest", v)}
              className="cursor-pointer"
            />
          </div>
        </div>
      </section>

      {/* Delete teacher data */}
      <section>
        <div className="flex items-center gap-2 mb-1">
          <Trash2 className="w-4 h-4 text-destructive" />
          <h2 className="text-base font-semibold text-foreground">Delete teacher data</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Remove your teacher profile, offerings, and all inquiry threads.
          Your student account (if you have one) remains intact.
        </p>
        <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-4">
          <p className="text-sm text-foreground mb-3">
            A confirmation email will be sent first. Clicking the link permanently removes your
            teacher data. This cannot be undone.
          </p>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="destructive"
                size="sm"
                disabled={initiateTeacherDeletion.isPending}
                className="cursor-pointer"
              >
                Delete teacher data
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete your teacher data?</AlertDialogTitle>
                <AlertDialogDescription>
                  This removes your teacher profile, offerings, and inquiry history. A
                  confirmation email will be sent — clicking it executes the deletion. Your
                  student account (if any) is unaffected. This cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Keep my data</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  onClick={handleTeacherDelete}
                >
                  Send confirmation email
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </section>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Page root
// ────────────────────────────────────────────────────────────────────────────

export default function SettingsPage() {
  usePageTitle('Settings | BookMyCoaching');
  const [view, setView] = useState<"content" | "search">("content");
  const { user: currentUser, refetch: refetchCurrentUser } = useCurrentUser();
  const { role } = useRole();
  const { teacherProfile } = useTeacherProfile();

  const hasStudentRole = currentUser?.roles?.some((r) => r.role === "STUDENT") ?? false;
  const hasTeacherRole = currentUser?.roles?.some((r) => r.role === "TEACHER") ?? false;
  const isTeacher = role === "teacher" || currentUser?.defaultRole === "TEACHER";
  const displayName =
    teacherProfile?.fullName || currentUser?.name || (isTeacher ? "Teacher" : "Student");

  return (
    <SidebarProvider>
      <DashboardTopNav role="student" />
      <div className={`flex min-h-screen w-full bg-background pt-14 ${isTeacher ? "teacher-theme" : "student-theme"}`}>
        {isTeacher ? (
          <TeacherSidebar teacherName={displayName} teacherAvatar={teacherProfile?.profileImage || ""} />
        ) : (
          <StudentSidebar
            studentName={displayName}
            studentAvatar=""
            onFindTeachers={() => setView("search")}
            findTeachersActive={view === "search"}
          />
        )}

        <div className="flex-1 flex flex-col min-w-0">
          {!isTeacher && view === "search" ? (
            <SearchResultsPage embedded onBack={() => setView("content")} />
          ) : (
          <main className="flex-1 px-6 py-8">
            <Tabs defaultValue="general" className="max-w-2xl">
              <TabsList className="mb-8">
                <TabsTrigger value="general">General</TabsTrigger>
                {hasStudentRole && <TabsTrigger value="student">Student</TabsTrigger>}
                {hasTeacherRole && <TabsTrigger value="teacher">Teacher</TabsTrigger>}
              </TabsList>

              <TabsContent value="general">
                <GeneralTab
                  name={currentUser?.name ?? ""}
                  phoneNumber={currentUser?.phoneNumber}
                  email={currentUser?.email}
                  onSaved={() => refetchCurrentUser()}
                />
              </TabsContent>

              {hasStudentRole && (
                <TabsContent value="student">
                  <StudentTab displayName={displayName} />
                </TabsContent>
              )}

              {hasTeacherRole && (
                <TabsContent value="teacher">
                  <TeacherTab />
                </TabsContent>
              )}
            </Tabs>
          </main>
          )}
        </div>
      </div>
    </SidebarProvider>
  );
}
